import type { editedPageProperties } from 'reduxStore/edit/types'
import type { DrawingSet, Project, ProjectsState, drawingPage } from './types'

export const selectProjectsList = (state: { projects: ProjectsState }) => state.projects.projectsList
export const selectArchivedProjectsList = (state: { projects: ProjectsState }) => state.projects.archivedProjects
export const selectProjectsLoading = (state: { projects: ProjectsState }) => state.projects.loading
export const selectFolderList = (state: { projects: ProjectsState }) => state.projects.folders
export const selectDrawingSets = (state: { projects: ProjectsState }) => state.projects.drawingSets

// Pure helpers for consumers that hold the project from context rather than
// the store (EditMode, UnifiedSidebar). Pages with a null/undefined
// drawingSetId belong to the ORIGINAL set.

export const getOriginalSetId = (drawingSets: DrawingSet[]) => {
  return drawingSets.find((set) => set.setType === 'ORIGINAL')?.id ?? null
}

export const getActiveSetId = (drawingSets: DrawingSet[], project: Project | null) => {
  if (!project || drawingSets.length === 0) return null
  return project.activeDrawingSetId ?? getOriginalSetId(drawingSets)
}

export const isPageInSet = (page: drawingPage, setId: string | null, drawingSets: DrawingSet[]) => {
  if (setId === null) return true
  if (page.drawingSetId) return page.drawingSetId === setId
  // Unstamped pages default to the Original set — UNLESS an upload is
  // processing, in which case they're its in-flight arrivals (set creation
  // stamps every pre-existing page, so nothing else can be unstamped).
  // Without this, a large revision inflates the Original set's counts and
  // pollutes compare candidates until every page is stamped.
  const processingSetId = drawingSets.find((set) => set.reviewStatus === 'PROCESSING')?.id
  return (processingSetId ?? getOriginalSetId(drawingSets)) === setId
}

// All pages when the project has no drawing sets yet.
export const getActiveSetPages = (drawingSets: DrawingSet[], project: Project | null): drawingPage[] => {
  if (!project) return []
  const activeSetId = getActiveSetId(drawingSets, project)
  if (activeSetId === null) return project.drawingPageList
  return project.drawingPageList.filter((page) => isPageInSet(page, activeSetId, drawingSets))
}
export const selectProposals = (state: { projects: ProjectsState }) => state.projects.proposals
export const selectPageDetails = (state: { projects: ProjectsState }) => state.projects.pageDetails
export const selectSubscription = (state: { projects: ProjectsState }) => state.projects.subscription
export const selectSubscriptionId = (state: { projects: ProjectsState }) => state.projects.subscriptionId
const utilsSelectorIndex = (state: { projects: ProjectsState }, ownProps: { editedPage: editedPageProperties }) => {
  const project = state.projects.projectsList?.find((item) => item.id === ownProps.editedPage.id)
  return project?.drawingPageList?.find((page) => page.id === ownProps.editedPage.pageId)
}

export const selectScale = (state: { projects: ProjectsState }, ownProps: { editedPage: editedPageProperties }) => {
  return utilsSelectorIndex(state, ownProps)?.drawingScale ?? {}
}

export const selectShowInfo = (state: { projects: ProjectsState }, ownProps: { editedPage: editedPageProperties }) => {
  return utilsSelectorIndex(state, ownProps)?.showInfo ?? true
}
